import React, {useContext}  from 'react';

import {copyComponentKeys} from '../lib/react/higher-order-components';
import OEEnvContextHelper from '../lib/tools/oe-env-context-helper';
import AppComponent from './app-component';
import AppConfig from './app-config';
import AppState from './app-state';

// data model for app environment state
export default class AppEnv {
    constructor(component, config, state) {
        this.component = component || new AppComponent();
        this.config = config || new AppConfig();
        this.state = state || new AppState();
    }
}

// define a context holding AppEnv
export const AppEnvContext = OEEnvContextHelper.createContext(AppEnv, 'AppEnv');

// custom hooks for AppEnvContext subscription
export const useAppEnv = OEEnvContextHelper.useEnv(AppEnvContext);

export function useAppComponent() {
    var ctx = useContext(AppEnvContext);
    var setter = function(comp) {
        ctx.setEnv(new AppEnv(Object.assign(ctx.env.component, comp), ctx.env.config, ctx.env.state));
    };
    var getter = function() {
        return ctx.env.component;
    };
    return [ctx.env.component, setter, getter];
}

export function useAppConfig() {
    var ctx = useContext(AppEnvContext);
    var setter = function(config) {
        ctx.setEnv(new AppEnv(ctx.env.component, {...ctx.env.config, ...config}, ctx.env.state));
    };
    var getter = function() {
        return ctx.env.config;
    };
    return [ctx.env.config, setter, getter];
}

export function useAppState() {
    var ctx = useContext(AppEnvContext);
    var setter = function(state) {
        ctx.setEnv(new AppEnv(ctx.env.component, ctx.env.config, {...ctx.env.state, ...state}));
    }
    var getter = function() {
        return ctx.env.state;
    };
    return [ctx.env.state, setter, getter];
}

// context providing component
export const AppEnvContextProvider = OEEnvContextHelper.contextProviderComponent(AppEnvContext, AppEnv);

function GlobalAppEnvHooks()    {
    const [component, setComponent, getComponent] = useAppComponent();
    const [state, setState, getState] = useAppState();
    const [config, setConfig, getConfig] = useAppConfig();

    global.getAppComponent = getComponent;

    global.getAppState = getState;
    global.setAppState = setState;

    global.getAppConfig = getConfig;
    return null;
}

// HOC for embedding given App into an AppEnvContext
export const withAppEnv = OEEnvContextHelper.withContext(AppEnvContextProvider, GlobalAppEnvHooks);

// HOCs for adding environment as env prop
export function addAppEnv(WrappedComponent) {
    return copyComponentKeys(function(props)  {
        const [appEnv, setAppEnv, updateAppEnv] = useAppEnv();
        return <WrappedComponent appEnv={appEnv} setAppEnv={setAppEnv} updateAppEnv={updateAppEnv} {...props} />;
    }, WrappedComponent);
}

export function assembleAppEnv(WrappedComponent) {
    return copyComponentKeys(class extends React.PureComponent {

        constructor(props) {
            super(props);
            this.state = this.constructState(this.props);
        }

        constructState(props)   {
            return {
                set: props.setAppEnv,
                update: props.updateAppEnv,
                component: props.appEnv.component,
                setComponent: function(comp) {
                    Object.assign(props.appEnv.component, comp);
                },
                config: props.appEnv.config,
                setConfig: function(config) {
                    props.setAppEnv((appEnv) => {
                        return new AppEnv(appEnv.component, {...appEnv.config, ...config}, appEnv.state)
                    });
                },
                state: props.appEnv.state,
                setState: function(state) {
                    props.setAppEnv((appEnv) => {
                        return new AppEnv(appEnv.component, appEnv.config, {...appEnv.state, ...state});
                    });
                }
            };
        }

        componentWillReceiveProps(nextProps) {
            if(this.props.appEnv !== nextProps.appEnv || this.props.setAppEnv !== nextProps.setAppEnv || this.props.updateAppEnv !== nextProps.updateAppEnv) {
                this.setState(this.constructState(nextProps))
            }
        }

        render() {
            const props = {...this.props};
            delete props.appEnv;
            delete props.setAppEnv;
            delete props.updateAppEnv;
            return <WrappedComponent appEnv={this.state} {...props} />;
        }

    }, WrappedComponent);
}

export function addAssembledAppEnv(WrappedComponent) {
    return addAppEnv(assembleAppEnv(WrappedComponent));
}

// creates HOCs for global state reduction defined by given function constructState: (assembled AppEnv) => props for WrappedComponent
export function connectAppEnv(constructState) {
    return (WrappedComponent) => {
        return copyComponentKeys(addAssembledAppEnv(class extends React.PureComponent {

            constructor(props) {
                super(props);
                this.state = constructState(this.props.appEnv);
            }

            componentWillReceiveProps(nextProps) {
                if(this.props.appEnv !== nextProps.appEnv) {
                    this.setState(constructState(nextProps.appEnv))
                }
            }

            render() {
                let {appEnv, ...rest} = this.props;

                if(WrappedComponent.defaultProps)   {
                    Object.keys(this.state).forEach(key => {
                        if(rest[key] === WrappedComponent.defaultProps[key]) delete rest[key];
                    });
                } else {
                    Object.keys(this.state).forEach(key => {
                        delete rest[key];
                    });
                }

                return <WrappedComponent {...this.state} {...rest}/>;
            }

        }), WrappedComponent);
    };
}